import { useState } from "react";
import { useConfigEditor, SaveBar, Field, TextArea, ImageInput, Section } from "../../lib/admin-shared";
import { BLOCK_TYPES, newBlock, duplicateBlock } from "../../lib/homepage-blocks";
import { Plus, Copy, Trash2, ChevronUp, ChevronDown, Layers, X } from "lucide-react";

export default function HomepageEditor() {
    const { cfg, set, save, saving, saved, loading } = useConfigEditor();
    const [showLibrary, setShowLibrary] = useState(false);

    if (loading || !cfg) return <div className="text-muted-foreground">Loading…</div>;
    const blocks = cfg.homepage_blocks || [];

    const setBlocks = (next) => set("homepage_blocks", next);

    const addBlock = (type) => {
        const b = newBlock(type);
        if (b) setBlocks([...blocks, b]);
        setShowLibrary(false);
    };
    const dupBlock = (i) => {
        const next = [...blocks];
        next.splice(i + 1, 0, duplicateBlock(blocks[i]));
        setBlocks(next);
    };
    const delBlock = (i) => {
        if (!window.confirm("Remove this block?")) return;
        setBlocks(blocks.filter((_, idx) => idx !== i));
    };
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= blocks.length) return;
        const next = [...blocks];
        [next[i], next[j]] = [next[j], next[i]];
        setBlocks(next);
    };
    const updateBlock = (i, dataPatch) => {
        const next = [...blocks];
        next[i] = { ...next[i], data: { ...next[i].data, ...dataPatch } };
        setBlocks(next);
    };

    return (
        <div className="max-w-4xl space-y-8" data-testid="homepage-editor">
            <div>
                <h1 className="font-display text-4xl mb-2">Homepage</h1>
                <p className="text-muted-foreground">The fixed hero, mission, and featured sections stay above. Custom blocks below render between the news section and the footer CTA.</p>
            </div>

            <Section title="Hero & mission (fixed)" description="The top of the homepage — always shown.">
                <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Hero image</span>
                    <ImageInput value={cfg.home_hero_image} onChange={(v) => set("home_hero_image", v)} testid="hp-hero-image" />
                </div>

                {/* Hero animation & overlay */}
                <div className="border-t border-border pt-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-primary mb-3">Hero image animation & overlay</div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Animation style</span>
                            <select value={cfg.hero_animation || "ken-burns"} onChange={(e) => set("hero_animation", e.target.value)}
                                className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="hp-hero-animation">
                                <option value="ken-burns">Ken Burns (slow zoom + pan)</option>
                                <option value="zoom">Zoom-in on load</option>
                                <option value="fade">Fade in</option>
                                <option value="parallax">Parallax on scroll</option>
                                <option value="static">Static (no animation)</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Overlay style</span>
                            <select value={cfg.hero_overlay_style || "dark"} onChange={(e) => set("hero_overlay_style", e.target.value)}
                                className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="hp-hero-overlay">
                                <option value="dark">Dark gradient (default)</option>
                                <option value="light">Light gradient</option>
                                <option value="primary">Primary color tint</option>
                                <option value="accent">Accent color tint</option>
                                <option value="none">None</option>
                            </select>
                        </label>
                        <Field label="Overlay opacity (0–100)" type="number" value={cfg.hero_overlay_opacity} onChange={(v) => set("hero_overlay_opacity", Math.max(0, Math.min(100, parseInt(v) || 0)))} testid="hp-hero-opacity" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Tip: Use a lower opacity (30–45) for lighter hero images, higher (55–75) for busy or bright images so the text remains legible.</p>
                </div>

                <Field label="Hero title" value={cfg.home_hero_title} onChange={(v) => set("home_hero_title", v)} testid="hp-hero-title" />
                <TextArea label="Hero subtitle" value={cfg.home_hero_subtitle} onChange={(v) => set("home_hero_subtitle", v)} testid="hp-hero-subtitle" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="CTA 1 label" value={cfg.home_cta_primary_label} onChange={(v) => set("home_cta_primary_label", v)} testid="hp-cta1-label" />
                    <Field label="CTA 1 link" value={cfg.home_cta_primary_link} onChange={(v) => set("home_cta_primary_link", v)} testid="hp-cta1-link" />
                    <Field label="CTA 2 label" value={cfg.home_cta_secondary_label} onChange={(v) => set("home_cta_secondary_label", v)} testid="hp-cta2-label" />
                    <Field label="CTA 2 link" value={cfg.home_cta_secondary_link} onChange={(v) => set("home_cta_secondary_link", v)} testid="hp-cta2-link" />
                </div>
                <TextArea label="Mission statement (large quote)" value={cfg.home_mission_text} onChange={(v) => set("home_mission_text", v)} rows={3} testid="hp-mission" />
                <TextArea label="Supporting paragraph" value={cfg.home_supporting_text} onChange={(v) => set("home_supporting_text", v)} testid="hp-supporting" />
            </Section>

            <Section title="Custom content blocks" description="Build with copy-pasteable sections. Reorder, duplicate, or remove any block.">
                <div className="space-y-4" data-testid="homepage-blocks">
                    {blocks.length === 0 && (
                        <div className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-sm">
                            No custom blocks yet. Click "Add block" below to insert one.
                        </div>
                    )}
                    {blocks.map((b, i) => (
                        <BlockEditor key={b.id || i} block={b} index={i} total={blocks.length}
                            onUpdate={(patch) => updateBlock(i, patch)}
                            onDup={() => dupBlock(i)} onDel={() => delBlock(i)}
                            onUp={() => move(i, -1)} onDown={() => move(i, 1)} />
                    ))}
                </div>
                <button type="button" onClick={() => setShowLibrary(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-sm font-medium"
                    data-testid="add-block-button">
                    <Plus size={14} /> Add block from library
                </button>
            </Section>

            <SaveBar onSave={save} saving={saving} saved={saved} />

            {showLibrary && <Library onPick={addBlock} onClose={() => setShowLibrary(false)} />}
        </div>
    );
}

const BlockEditor = ({ block, index, total, onUpdate, onDup, onDel, onUp, onDown }) => {
    const meta = BLOCK_TYPES[block.type];
    const d = block.data || {};
    return (
        <div className="bg-card border border-border rounded-sm" data-testid={`block-${block.id}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
                <div className="inline-flex items-center gap-2">
                    <Layers size={14} className="text-primary" />
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">#{index + 1} · {meta?.label || block.type}</span>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={onUp} disabled={index === 0} className="p-1.5 rounded-sm hover:bg-background disabled:opacity-30" data-testid={`block-up-${block.id}`}><ChevronUp size={14} /></button>
                    <button onClick={onDown} disabled={index === total - 1} className="p-1.5 rounded-sm hover:bg-background disabled:opacity-30" data-testid={`block-down-${block.id}`}><ChevronDown size={14} /></button>
                    <button onClick={onDup} className="p-1.5 rounded-sm hover:bg-background" title="Duplicate" data-testid={`block-dup-${block.id}`}><Copy size={14} /></button>
                    <button onClick={onDel} className="p-1.5 rounded-sm hover:bg-destructive/10 text-destructive" title="Delete" data-testid={`block-del-${block.id}`}><Trash2 size={14} /></button>
                </div>
            </div>
            <div className="p-4 space-y-4">
                <BlockFields type={block.type} data={d} onChange={(patch) => onUpdate(patch)} />
            </div>
        </div>
    );
};

const BlockFields = ({ type, data, onChange }) => {
    const upd = (k, v) => onChange({ [k]: v });
    switch (type) {
        case "heading":
            return (<>
                <Field label="Eyebrow (small label above)" value={data.eyebrow} onChange={(v) => upd("eyebrow", v)} testid="bf-eyebrow" />
                <Field label="Title" value={data.title} onChange={(v) => upd("title", v)} testid="bf-title" />
                <TextArea label="Subtitle" value={data.subtitle} onChange={(v) => upd("subtitle", v)} rows={2} testid="bf-subtitle" />
            </>);
        case "text":
            return (<>
                <Field label="Title (optional)" value={data.title} onChange={(v) => upd("title", v)} testid="bf-title" />
                <TextArea label="Body" value={data.body} onChange={(v) => upd("body", v)} rows={6} testid="bf-body" />
            </>);
        case "image_text":
            return (<>
                <Field label="Title" value={data.title} onChange={(v) => upd("title", v)} testid="bf-title" />
                <TextArea label="Body" value={data.body} onChange={(v) => upd("body", v)} rows={5} testid="bf-body" />
                <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Image</span>
                    <ImageInput value={data.image_url} onChange={(v) => upd("image_url", v)} testid="bf-image" />
                </div>
                <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Layout</span>
                    <select value={data.layout || "image-left"} onChange={(e) => upd("layout", e.target.value)}
                        className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="bf-layout">
                        <option value="image-left">Image left, text right</option>
                        <option value="image-right">Image right, text left</option>
                    </select>
                </label>
            </>);
        case "quote":
            return (<>
                <TextArea label="Quote" value={data.quote} onChange={(v) => upd("quote", v)} rows={3} testid="bf-quote" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Attribution" value={data.attribution} onChange={(v) => upd("attribution", v)} testid="bf-attribution" />
                    <Field label="Role / location" value={data.role} onChange={(v) => upd("role", v)} testid="bf-role" />
                </div>
            </>);
        case "stats": {
            const stats = data.stats || [];
            const setStat = (i, key, v) => {
                const next = stats.map((s, idx) => idx === i ? { ...s, [key]: v } : s);
                onChange({ stats: next });
            };
            const addStat = () => onChange({ stats: [...stats, { value: "", label: "" }] });
            const delStat = (i) => onChange({ stats: stats.filter((_, idx) => idx !== i) });
            return (<>
                <Field label="Eyebrow (e.g. 'Our impact')" value={data.eyebrow} onChange={(v) => upd("eyebrow", v)} testid="bf-eyebrow" />
                <div className="space-y-3">
                    {stats.map((s, i) => (
                        <div key={i} className="flex items-end gap-2">
                            <Field label="Value" value={s.value} onChange={(v) => setStat(i, "value", v)} testid={`bf-stat-value-${i}`} />
                            <Field label="Label" value={s.label} onChange={(v) => setStat(i, "label", v)} testid={`bf-stat-label-${i}`} />
                            <button type="button" onClick={() => delStat(i)} className="px-3 py-3 border border-destructive text-destructive rounded-sm text-xs"><Trash2 size={12} /></button>
                        </div>
                    ))}
                    <button type="button" onClick={addStat} className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-border text-xs"><Plus size={12} /> Add stat</button>
                </div>
            </>);
        }
        case "cta":
            return (<>
                <Field label="Eyebrow" value={data.eyebrow} onChange={(v) => upd("eyebrow", v)} testid="bf-eyebrow" />
                <Field label="Title" value={data.title} onChange={(v) => upd("title", v)} testid="bf-title" />
                <TextArea label="Body" value={data.body} onChange={(v) => upd("body", v)} rows={2} testid="bf-body" />
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Button label" value={data.button_label} onChange={(v) => upd("button_label", v)} testid="bf-btn-label" />
                    <Field label="Button link" value={data.button_link} onChange={(v) => upd("button_link", v)} testid="bf-btn-link" />
                </div>
            </>);
        case "spacer":
            return <Field label="Height (px)" type="number" value={data.size} onChange={(v) => upd("size", parseInt(v) || 60)} testid="bf-size" />;
        default:
            return null;
    }
};

const Library = ({ onPick, onClose }) => (
    <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="block-library">
        <div className="bg-card border border-border rounded-sm w-full max-w-2xl my-10">
            <div className="flex items-center justify-between p-6 border-b border-border">
                <div>
                    <h2 className="font-display text-2xl">Block library</h2>
                    <p className="text-xs text-muted-foreground mt-1">Pick a block type to insert. You can edit it inline after.</p>
                </div>
                <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-3">
                {Object.entries(BLOCK_TYPES).map(([key, meta]) => (
                    <button key={key} onClick={() => onPick(key)}
                        className="text-left p-4 rounded-sm border border-border hover:border-primary/60 hover:bg-muted/30 transition-colors"
                        data-testid={`lib-${key}`}>
                        <div className="font-medium mb-1">{meta.label}</div>
                        <div className="text-xs text-muted-foreground">Insert a "{meta.label.toLowerCase()}" block.</div>
                    </button>
                ))}
            </div>
        </div>
    </div>
);
